/**
 * useChatSessionMachine — the explicit lifecycle state machine for the active
 * chat session in AIChatSidePanel.
 *
 * Replaces the scattered effect/ref dance that decided "which session is active
 * and what is it doing" (the `hasAutoSelectedRef`-guarded auto-select effect,
 * the inline basic-mode auto-select, the ad-hoc `reset()` calls in New
 * Chat / switch, and the implicit streaming flags) with ONE machine whose
 * `phase` makes the lifecycle explicit, so open / auto-select / New Chat /
 * switch / send / Stop can never interleave into a bad state.
 *
 * Builds on A1: `useChatRouting` stays the SINGLE owner of the session id; this
 * machine never duplicates it. The reducer holds only `phase`; transitions that
 * change the active session call the routing setter passed in.
 *
 * Phases:
 *   idle         panel closed — no session activity.
 *   selecting    panel just opened — resolving which session to bind (a
 *                deep-link id already present, the most-recent session, or none).
 *   bound        a session (existing or brand-new/empty) is active, not streaming.
 *   streaming    a turn is in flight (the server runner is generating).
 *   interrupting Stop was pressed mid-stream — awaiting the partial's finalize.
 *
 * Transitions are driven two ways, both explicit:
 *   - User actions call the returned callbacks: open/close (the `open` prop),
 *     `newChat`, `switchSession`, `stop`.
 *   - The authoritative `isGenerating` flag from `useChatSession` bridges turn
 *     start/finish (TURN_STARTED / TURN_SETTLED). A turn driven by the user, a
 *     reconnect that finds one running, the runner's `done`/`error`, and the
 *     `interrupted` reply ALL resolve through the same edge, so the machine
 *     can't desync from what the socket is actually doing.
 *
 * The PHASE itself is the run-once guard: auto-select fires only while
 * `phase === 'selecting'`, and leaving that phase (BIND) makes it unrepeatable
 * — there is no standalone `hasAutoSelectedRef` boolean anymore.
 *
 * Interrupt is first-class: Stop moves `streaming → interrupting`, and the
 * machine resolves cleanly back to `bound` whenever `isGenerating` clears —
 * whether the `interrupted` event arrives, the WebSocket drops (reconnect
 * exhausted), or the user switches away first. While `interrupting`, a new turn
 * cannot start (TURN_STARTED only fires from `bound`/`selecting`), which is what
 * guarantees the partial assistant message finalizes exactly once before any
 * follow-up turn.
 */
import { useReducer, useEffect, useCallback } from 'react';

export type ChatPhase =
  | 'idle'
  | 'selecting'
  | 'bound'
  | 'streaming'
  | 'interrupting';

export type ChatMachineEvent =
  | { type: 'OPEN' }
  | { type: 'CLOSE' }
  | { type: 'RESELECT' }
  | { type: 'BIND' }
  | { type: 'NEW_CHAT' }
  | { type: 'SWITCH' }
  | { type: 'TURN_STARTED' }
  | { type: 'STOP' }
  | { type: 'TURN_SETTLED' };

export interface ChatMachineState {
  phase: ChatPhase;
}

/**
 * Pure transition function — exported for unit testing. Every transition is a
 * no-op (returns the same state object) when it isn't valid from the current
 * phase, so stray events from racing effects can't corrupt the machine.
 */
export function chatSessionMachineReducer(
  state: ChatMachineState,
  event: ChatMachineEvent,
): ChatMachineState {
  const { phase } = state;
  switch (event.type) {
    case 'OPEN':
      // Re-open always re-enters selection so auto-select re-evaluates per open.
      return phase === 'idle' ? { phase: 'selecting' } : state;
    case 'CLOSE':
      return phase === 'idle' ? state : { phase: 'idle' };
    case 'RESELECT':
      // The panel re-targeted a new entity (uncontrolled mode); re-resolve which
      // session to bind for it. No-op when the panel is closed.
      return phase === 'idle' ? state : { phase: 'selecting' };
    case 'BIND':
      // Selection resolved (deep-link id, auto-selected most recent, or none).
      return phase === 'selecting' ? { phase: 'bound' } : state;
    case 'NEW_CHAT':
    case 'SWITCH':
      // Explicit user re-target from any open phase → a fresh bound session. Any
      // in-flight turn is abandoned (the caller resets the session hook).
      return phase === 'idle' ? state : { phase: 'bound' };
    case 'TURN_STARTED':
      // A turn began (user send, or a reconnect that found one running). Allowed
      // from `selecting` too: the very first message of a brand-new chat can set
      // `isGenerating` before auto-select has resolved.
      return phase === 'bound' || phase === 'selecting'
        ? { phase: 'streaming' }
        : state;
    case 'STOP':
      // First-class interrupt — only meaningful while a turn is streaming.
      return phase === 'streaming' ? { phase: 'interrupting' } : state;
    case 'TURN_SETTLED':
      // done / interrupted / error / unrecoverable-reconnect — the partial is
      // finalized (exactly once) by the session hook before this fires.
      return phase === 'streaming' || phase === 'interrupting'
        ? { phase: 'bound' }
        : state;
    default:
      return state;
  }
}

interface SessionListItem {
  id: string;
}

interface UseChatSessionMachineOptions {
  /** Whether the chat panel is open. Drives OPEN / CLOSE. */
  open: boolean;
  /** A1 single source of truth: the active session id. */
  sessionId: string | null;
  /** A1 synchronous setter for the active session id. */
  setSessionId: (sessionId: string | null) => void;
  /** Authoritative streaming flag from `useChatSession` — bridges turn edges. */
  isGenerating: boolean;
  /** Fully reset the session hook (close WS, drop messages/streaming state). */
  resetSession: () => void;
  /** POST /interrupt — set the runner's interrupt flag. */
  stopGeneration: () => void | Promise<void>;
  /**
   * The entity's session list (newest first), used to auto-select the most
   * recent session on open.
   */
  entitySessions: SessionListItem[];
  /**
   * True once the session list has settled (loaded or errored) for this open.
   * Auto-select waits for this so it doesn't prematurely bind to "no session".
   */
  sessionsReady: boolean;
  /**
   * Optional side-effect run at the start of an explicit New Chat / switch
   * transition (e.g. clearing a buffered pre-session model choice). Kept out of
   * the machine's core concern (session + phase) but deduped across both
   * transitions.
   */
  onResetTransient?: () => void;
}

export interface UseChatSessionMachineReturn {
  /** The explicit lifecycle phase. */
  phase: ChatPhase;
  /** Convenience: a turn is actively streaming. */
  isStreaming: boolean;
  /** Convenience: Stop was pressed and the interrupt hasn't resolved yet. */
  isInterrupting: boolean;
  /** Start a brand-new (empty) chat, abandoning any in-flight turn. */
  newChat: () => void;
  /** Switch to an existing session, abandoning any in-flight turn. */
  switchSession: (sessionId: string) => void;
  /** Interrupt the streaming turn (no-op unless actively streaming). */
  stop: () => void;
  /**
   * Re-enter selection for a newly-targeted entity (uncontrolled-mode entity
   * switch). Auto-select then re-resolves the most-recent session for it.
   */
  reselect: () => void;
}

export function useChatSessionMachine({
  open,
  sessionId,
  setSessionId,
  isGenerating,
  resetSession,
  stopGeneration,
  entitySessions,
  sessionsReady,
  onResetTransient,
}: UseChatSessionMachineOptions): UseChatSessionMachineReturn {
  const [state, dispatch] = useReducer(
    chatSessionMachineReducer,
    undefined,
    () => ({ phase: open ? ('selecting' as ChatPhase) : ('idle' as ChatPhase) }),
  );
  const { phase } = state;

  // Open / close edge → OPEN / CLOSE. (Mount with open=true is already
  // `selecting` via lazy init, so the mount OPEN is a no-op.)
  useEffect(() => {
    dispatch({ type: open ? 'OPEN' : 'CLOSE' });
  }, [open]);

  // Auto-select / deep-link resolution — runs ONLY while `selecting`, and the
  // phase is the guard: BIND leaves `selecting`, so this can't re-fire and
  // clobber a later New Chat. No `hasAutoSelectedRef` needed.
  useEffect(() => {
    if (phase !== 'selecting') return;
    if (sessionId) {
      // A deep-link / persisted id is already bound — adopt it, skip auto-select.
      dispatch({ type: 'BIND' });
      return;
    }
    if (!sessionsReady) return; // wait for the session list to settle
    if (entitySessions.length > 0) {
      // List is newest-first; bind the most recent session.
      setSessionId(entitySessions[0].id);
    }
    dispatch({ type: 'BIND' });
  }, [phase, sessionId, sessionsReady, entitySessions, setSessionId]);

  // Bridge the authoritative streaming flag to explicit turn edges. Level-
  // triggered (reconciles phase WITH `isGenerating`) rather than edge-triggered,
  // so the phase stays correct even when the flag was already set/cleared while
  // the machine wasn't watching — e.g. the panel is closed mid-stream and
  // reopened (useChatSession keeps streaming in the background): on reopen the
  // machine resolves to `bound`, then this effect syncs it up to `streaming` so
  // Stop still works. `interrupting` is preserved while the flag is still set
  // (we only settle once it clears).
  useEffect(() => {
    if (isGenerating && (phase === 'bound' || phase === 'selecting')) {
      dispatch({ type: 'TURN_STARTED' });
    } else if (!isGenerating && (phase === 'streaming' || phase === 'interrupting')) {
      dispatch({ type: 'TURN_SETTLED' });
    }
  }, [isGenerating, phase]);

  const newChat = useCallback(() => {
    onResetTransient?.();
    resetSession();
    setSessionId(null);
    dispatch({ type: 'NEW_CHAT' });
  }, [onResetTransient, resetSession, setSessionId]);

  const switchSession = useCallback(
    (nextSessionId: string) => {
      if (nextSessionId === sessionId) return;
      onResetTransient?.();
      resetSession();
      setSessionId(nextSessionId);
      dispatch({ type: 'SWITCH' });
    },
    [sessionId, onResetTransient, resetSession, setSessionId],
  );

  const stop = useCallback(() => {
    // Guard on the authoritative streaming flag (not just `phase`): a Stop is
    // valid whenever a turn is actually generating — including the brief window
    // after reopening mid-stream before the bridge has synced phase to
    // `streaming`. Excluding `interrupting` blocks a double-interrupt once Stop
    // has already been pressed. `dispatch(STOP)` only advances phase from
    // `streaming → interrupting`; from any other phase it's a harmless no-op
    // while `stopGeneration()` still fires the interrupt.
    if (!isGenerating || phase === 'interrupting') return;
    dispatch({ type: 'STOP' });
    void stopGeneration();
  }, [isGenerating, phase, stopGeneration]);

  // Re-resolve selection when the panel re-targets a new entity. The session id
  // is cleared by the caller (useChatRouting's resetKey in uncontrolled mode);
  // this just re-enters `selecting` so auto-select picks the new entity's most
  // recent session.
  const reselect = useCallback(() => {
    dispatch({ type: 'RESELECT' });
  }, []);

  return {
    phase,
    isStreaming: phase === 'streaming',
    isInterrupting: phase === 'interrupting',
    newChat,
    switchSession,
    stop,
    reselect,
  };
}
